/**
 * Refer to https://github.com/enzymejs/enzyme/issues/2176 for how to test React Contexts with a shallow
 * enzyme renderer
 */
import type { ReactNode } from "react";

import { createContext, useState, use } from "react";

const initialState: State = {
  username: undefined,
  admin: false,
};

type UserDetailsProviderProps = {
  children?: ReactNode;
};

// provided by useLoginDetails
type State = {
  username?: string;
  admin: boolean;
};
// provided by useSetLoginDetails
type SetState = (newState: State) => void;

const UserDetailsStateContext = createContext<State | undefined>(undefined);
UserDetailsStateContext.displayName = "UserDetailsStateContext";

const UserDetailsSetStateContext = createContext<SetState | undefined>(
  undefined,
);
UserDetailsSetStateContext.displayName = "UserDetailsSetStateContext";

const UserDetailsProvider = (props: UserDetailsProviderProps) => {
  const [userDetails, setUserDetails] = useState<State>(initialState);

  return (
    <UserDetailsStateContext value={userDetails}>
      <UserDetailsSetStateContext value={setUserDetails}>
        {props.children}
      </UserDetailsSetStateContext>
    </UserDetailsStateContext>
  );
};
function useLoginDetails() {
  const context = use(UserDetailsStateContext);
  if (context === undefined) {
    throw new Error(
      "useLoginDetails must be used within a UserDetailsProvider",
    );
  }
  return context;
}
function useSetLoginDetails() {
  const context = use(UserDetailsSetStateContext);
  if (context === undefined) {
    throw new Error(
      "useSetLoginDetails must be used within a UserDetailsProvider",
    );
  }
  return context;
}
/**
 * When needing to access the UserDetails Provider from within a Class based component, use this
 */
function UserDetailsConsumer({ children }: any) {
  return (
    <UserDetailsStateContext.Consumer>
      {(context) => {
        if (context === undefined) {
          throw new Error(
            "LoginConsumer must be used within a UserDetailsProvider",
          );
        }
        return children(context);
      }}
    </UserDetailsStateContext.Consumer>
  );
}
export {
  UserDetailsProvider,
  useLoginDetails,
  useSetLoginDetails,
  UserDetailsConsumer,
};
export { State as UserDetailsProviderState };
/*
 * Exported incase class components need to access UserDetails state.
 *
 * Use like this, within a <UserDetailsProvider> tag
 * class Component {
 *   static contextType = UserDetailsStateContext;
 *   context: React.ContextType<typeof UserDetailsStateContext>;
 * }
 */
export { UserDetailsStateContext, UserDetailsSetStateContext };
